import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import {
  Button,
  Badge,
  OverlayTrigger,
  Tooltip,
  Modal,
  InputGroup,
  FormControl,
} from 'react-bootstrap';
import RewardCard from '../../components/rewardCard/RewardCard';
import MapboxMap from '../../components/mapboxMap/MapboxMap';
// import MapboxHeatmap from '../../components/mapboxHeatmap/MapboxHeatmap';

import PlayerLeaderboard from '../../components/playerLeaderboard/PlayerLeaderboard';
import { AuthContext } from '../../context/AuthContext';
import QRScanCheckin from '../../components/qrScanCheckin/QRScanCheckin';
import BusinessDashboard from '../businessDashboard/BusinessDashboard';

import './businessPage.css';

function BusinessPage() {
  const { player } = useContext(AuthContext);
  const { businessId } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkInResult, setCheckInResult] = useState(null);
  const [playerCoords, setPlayerCoords] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [qrError, setQrError] = useState('');

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const { data } = await axiosConn.get(`businesses/${businessId}`, {
          headers: { Authorization: `Bearer ${player?.token}` },
        });
        setBusiness(data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchBusiness();
  }, [businessId, player?.token]);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => setPlayerCoords(pos.coords),
      () => console.warn('Geolocation not enabled'),
    );
  }, []);

  const handleCheckIn = async () => {
    if (!playerCoords) {
      alert('Location not available. Please enable GPS.');
      return;
    }
    try {
      const res = await axiosConn.post(
        '/checkins',
        {
          businessId,
          latitude: playerCoords.latitude,
          longitude: playerCoords.longitude,
          source: 'location',
        },
        {
          headers: { Authorization: `Bearer ${player?.token}` },
        },
      );
      setCheckInResult(res.data.data);
    } catch (err) {
      alert(err?.response?.data?.message || 'Check-in failed.');
    }
  };

  // QR code check-in (manual input, legacy)
  const handleQRCheckIn = async () => {
    setQrError('');
    if (!qrCode) {
      setQrError('Please enter a QR code.');
      return;
    }
    try {
      const res = await axiosConn.post(
        '/checkins',
        {
          businessId,
          qrCode,
          source: 'qr',
        },
        {
          headers: { Authorization: `Bearer ${player?.token}` },
        },
      );
      setCheckInResult(res.data.data);
      setShowQRModal(false);
      setQrCode('');
    } catch (err) {
      setQrError(err?.response?.data?.message || 'QR Check-in failed.');
    }
  };

  if (loading)
    return (
      <div className="text-center my-5">
        <div className="spinner-border" /> Loading...
      </div>
    );
  if (!business) return <p>Business not found</p>;

  // Fallbacks
  const fallbackPhoto =
    'https://res.cloudinary.com/joegalaxy/image/upload/v1753589314/68246674-your-business-here-rubber-stamp_ra7bvr.jpg';
  const fallbackLogo =
    'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png';

  // Attributes
  const {
    name,
    logo,
    photo,
    description,
    address,
    website,
    contactEmail,
    contactPhone,
    region,
    tags = [],
    isActive,
    hasClue,
    clue,
    location,
    checkIns = [],
    owner,
    managers = [],
    createdAt,
    updatedAt,
    _id,
    id,
    __v,
  } = business;

  const city = business?.city || '';
  const days = 30;
  const zoom = 15;
  // Map marker
  const coordinates = location?.coordinates;
  const mapMarkers = coordinates
    ? [
        {
          id: _id || id,
          coordinates,
          name,
        },
      ]
    : [];

  // Helper for info rows
  const InfoRow = ({ icon, label, value, href, tooltip }) =>
    value ? (
      <OverlayTrigger
        placement="top"
        overlay={tooltip ? <Tooltip>{tooltip}</Tooltip> : <></>}
      >
        <div className="business-info-row">
          <span>{icon}</span>
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: 500 }}
            >
              {value}
            </a>
          ) : (
            <span style={{ fontWeight: 500 }}>{value}</span>
          )}
        </div>
      </OverlayTrigger>
    ) : null;

  // Flair for badges
  const flairBadge = (text, color, icon, extra = {}) => (
    <Badge
      bg={color}
      className="me-2 business-hero-badges"
      style={{
        fontSize: '1em',
        padding: '0.38em 1em',
        borderRadius: '14px',
        fontWeight: 700,
        letterSpacing: '0.5px',
        boxShadow: '0 2px 8px #0001',
        ...extra,
      }}
    >
      {icon} {text}
    </Badge>
  );

  // Animated clue badge
  const clueBadge =
    hasClue || clue
      ? flairBadge('Clue', 'success', '🧩', { animation: 'tada 1.2s' })
      : null;

  // Unclaimed badge
  const unclaimedBadge = !owner
    ? flairBadge('Unclaimed', 'warning', '🚩')
    : null;

  // Inactive badge
  const inactiveBadge = !isActive
    ? flairBadge('Inactive', 'secondary', '⏸️')
    : null;

  // Manager/Owner
  const ownerName = owner?.name || owner?.email || '';
  const managerNames = managers.map((m) => m.name || m.email).join(', ');

  // console.log('MapboxHeatmap props', region, city, days, businessId, zoom);

  return (
    <div className="business-page-container">
      {/* HERO HEADER */}
      <div className="business-hero">
        <div className="d-flex align-items-center mb-3 mb-md-0">
          <img
            src={logo || fallbackLogo}
            alt={name + ' logo'}
            className="business-hero-logo"
          />
          <div>
            <h2 className="business-hero-title">{name}</h2>
            <div className="business-hero-badges">
              {clueBadge}
              {unclaimedBadge}
              {inactiveBadge}
            </div>
          </div>
        </div>
        <img
          src={photo || fallbackPhoto}
          alt={name + ' photo'}
          className="business-hero-photo business-hero-photo-large"
        />
      </div>

      {/* CHECK-IN BUTTON (always just below hero, both mobile & desktop) */}
      <div className="business-checkin-flair">
        <Button
          onClick={handleCheckIn}
          variant="success"
          size="lg"
          className="flair-checkin-btn animated-subscribe-btn"
        >
          <span className="flair-emoji" role="img" aria-label="pin">
            📍
          </span>
          <span className="flair-text">Check In</span>
        </Button>
        <Button
          variant="primary"
          size="lg"
          className="flair-qr-btn ms-2 animated-subscribe-btn"
          onClick={() => setShowQRModal(true)}
        >
          <span className="flair-emoji" role="img" aria-label="qr">
            � QR
          </span>
          <span className="flair-text">QR Check-In</span>
        </Button>
        {checkInResult && (
          <div className="mt-4">
            <h5>✅ Rewards Earned</h5>
            <RewardCard rewards={checkInResult.rewards} />
          </div>
        )}
      </div>

      {/* QR Modal with visual scanner and manual entry */}
      <Modal show={showQRModal} onHide={() => setShowQRModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>QR Code Check-In</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <QRScanCheckin businessId={businessId} disabled={false} />
        </Modal.Body>
      </Modal>

      {/* INFO GRID */}
      <div className="row business-info-grid">
        <div className="col-md-6 col-lg-5 mb-3">
          <div className="business-info-card">
            <h5>
              <span role="img" aria-label="info">
                ℹ️
              </span>{' '}
              Business Info
            </h5>
            <InfoRow icon="📍" label="Address" value={address} />
            <InfoRow
              icon="🌐"
              label="Website"
              value={website}
              href={
                website &&
                (website.startsWith('http') ? website : `https://${website}`)
              }
            />
            <InfoRow
              icon="📧"
              label="Email"
              value={contactEmail}
              href={contactEmail && `mailto:${contactEmail}`}
            />
            <InfoRow
              icon="📞"
              label="Phone"
              value={contactPhone}
              href={contactPhone && `tel:${contactPhone}`}
            />
            <InfoRow icon="🌎" label="Region" value={region} />
            <InfoRow icon="🧑‍💼" label="Owner" value={ownerName} />
            <InfoRow icon="👥" label="Managers" value={managerNames} />
            <InfoRow icon="🆔" label="Business ID" value={_id || id} />
            <InfoRow icon="🔢" label="Version" value={__v} />
          </div>
          <div className="business-info-card">
            <h5>
              <span role="img" aria-label="tags">
                🏷️
              </span>{' '}
              Tags
            </h5>
            <div className="business-tags">
              {tags.length > 0 ? (
                tags.map((tag, i) => (
                  <Badge key={i} bg="info">
                    #{tag}
                  </Badge>
                ))
              ) : (
                <span className="text-muted">No tags</span>
              )}
            </div>
          </div>
          <div className="business-info-card">
            <h5>
              <span role="img" aria-label="meta">
                📊
              </span>{' '}
              Meta
            </h5>
            <div className="business-meta-row">
              <InfoRow
                icon="✅"
                label="Active"
                value={isActive ? 'Yes' : 'No'}
              />
              <InfoRow
                icon="🧩"
                label="Has Clue"
                value={hasClue ? 'Yes' : 'No'}
              />
              <InfoRow
                icon="🔄"
                label="Updated"
                value={updatedAt && new Date(updatedAt).toLocaleString()}
              />
              <InfoRow
                icon="🕒"
                label="Created"
                value={createdAt && new Date(createdAt).toLocaleString()}
              />
              <InfoRow icon="📈" label="Check-ins" value={checkIns.length} />
            </div>
          </div>
        </div>
        <div className="col-md-6 col-lg-7 mb-3">
          <div className="business-description-card">
            <h5>
              <span role="img" aria-label="desc">
                📝
              </span>{' '}
              Description
            </h5>
            <div>
              {description || (
                <span className="text-muted">No description provided.</span>
              )}
            </div>
          </div>
          <div
            className="business-dashboard-link-card"
            style={{ margin: '32px 0' }}
          >
            <Link
              to={`/business-dashboard/${business._id}`}
              className="btn btn-outline-primary"
              style={{
                display: 'inline-block',
                fontWeight: 600,
                fontSize: '1.1em',
              }}
            >
              📊 View Analytics Dashboard
            </Link>
          </div>
          {clue && (
            <div className="business-clue-card">
              <h5>
                <span role="img" aria-label="clue">
                  🧩
                </span>{' '}
                Clue
              </h5>
              <div style={{ fontSize: '1.13em', fontWeight: 500 }}>
                {clue.text}
              </div>
            </div>
          )}
          {coordinates && (
            <div className="business-map-card">
              <h5>
                <span role="img" aria-label="map">
                  🗺️
                </span>{' '}
                Location
              </h5>
              <MapboxMap
                markers={mapMarkers}
                center={coordinates}
                zoom={16}
                height="260px"
              />
            </div>
          )}
          {/* 🌎 World-Class, Modern, Fun Mapbox Heatmap */}
          {/* <div
            className="business-heatmap-card"
            style={{
              margin: '32px 0',
              borderRadius: 24,
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.10)',
              overflow: 'hidden',
              background: 'linear-gradient(120deg, #f1f5f9 60%, #e0e7ff 100%)',
              border: '1px solid #e0e7ef',
              padding: 0,
            }}
          >
            <div
              style={{
                background: 'rgba(30,41,59,0.92)',
                color: '#fff',
                padding: '18px 32px 10px 32px',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span style={{ fontSize: 28, marginRight: 8 }}>🔥</span>
              <h5
                className="mb-0"
                style={{
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  letterSpacing: 0.5,
                }}
              >
                Recent Check-In Heatmap
              </h5>
              <span
                className="badge bg-secondary ms-auto"
                style={{ fontSize: 15, background: '#818cf8' }}
              >
                {region}
              </span>
            </div>
            <div
              style={{ width: '100%', minHeight: 320, background: '#181f2a' }}
            >
              <MapboxHeatmap
                region={region}
                city={city}
                days={days}
                huntId={null}
                businessId={businessId}
                zoom={zoom}
              />
            </div>
          </div> */}
          <div className="business-leaderboard-card">
            <h5>
              <span role="img" aria-label="leaderboard">
                🏆
              </span>{' '}
              Visitors Leaderboard
            </h5>
            <PlayerLeaderboard
              businessId={businessId}
              region={region}
              limit={10}
            />
          </div>
        </div>
      </div>

      {/* 🌟 World-Class Business Analytics Dashboard
      
      <div
        className="business-dashboard-section"
        style={{
          margin: '48px 0 0 0',
          borderRadius: 28,
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.12)',
          background: 'linear-gradient(120deg, #e0e7ff 60%, #f1f5f9 100%)',
          border: '1px solid #e0e7ef',
          padding: '32px 0 24px 0',
        }}
      >
        <div
          style={{
            background: 'rgba(30,41,59,0.96)',
            color: '#fff',
            padding: '22px 40px 16px 40px',
            borderTopLeftRadius: 28,
            borderTopRightRadius: 28,
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <span style={{ fontSize: 32, marginRight: 10 }}>📊</span>
          <h4
            className="mb-0"
            style={{
              fontWeight: 800,
              fontSize: '1.45rem',
              letterSpacing: 0.5,
              textShadow: '0 2px 8px #0002',
            }}
          >
            Business Analytics Dashboard
          </h4>
          <span
            className="badge bg-info ms-auto"
            style={{
              fontSize: 16,
              background: '#818cf8',
              color: '#fff',
              fontWeight: 600,
              letterSpacing: 0.5,
            }}
          >
            {business?.name}
          </span>
        </div>
      </div> */}
    </div>
  );
}

export default BusinessPage;
